import { type KeyboardEvent, useState } from 'react';
import { rich, useT } from '@/i18n/runtime';
import type { TenantConfig } from '@/types';
import type { ConsentGateState } from '@/media/useConsent';
import type { ConsentPurposeKey } from '@/api/consent';
import { AppBar } from './AppBar';
import { Icon, type IconName } from './Icon';
import { Button } from './Button';

/**
 * CONSENT GATE — the DPDP consent ceremony shown on the call flow BEFORE the geo
 * gate (S7-7; XC-01 · IFSCA-05 · INV-6). The real, binding capture the notice-only
 * landing copy was deferring to: layered plain-language purposes, an explicit
 * un-ticked affirmative, the honest UNRATIFIED stamp, and — unlike geo — a genuine
 * GATE: declining cannot proceed to the regulated call, but the customer is told
 * plainly why and can reconsider.
 *
 * Tokens/classes only (Dataroom mode) — no hardcoded colours.
 */

// Exported for the Consent STEP (S7-6/S7-7): the pre-capture ceremony renders
// the same purpose copy and the same UNRATIFIED stamp — one source of truth.
export const PURPOSE_META: Record<ConsentPurposeKey, { icon: IconName; titleKey: string; descKey: string }> = {
  RECORDED_VIDEO: {
    icon: 'cam',
    titleKey: 'droom.consent.purpose.video.t',
    descKey: 'droom.consent.purpose.video.d',
  },
  BIOMETRIC_LIVENESS: {
    icon: 'face',
    titleKey: 'droom.consent.purpose.bio.t',
    descKey: 'droom.consent.purpose.bio.d',
  },
  LOCATION: {
    icon: 'mappin',
    titleKey: 'droom.consent.purpose.loc.t',
    descKey: 'droom.consent.purpose.loc.d',
  },
  DATA_RETENTION: {
    icon: 'lock',
    titleKey: 'droom.consent.purpose.ret.t',
    descKey: 'droom.consent.purpose.ret.d',
  },
  // S5-14 consent-v3 (RBI): the fraud-screening disclosure gets its own tile — the notice body
  // carries the full clause; older consent versions simply never send this key.
  FRAUD_DEDUP_SCREENING: {
    icon: 'shield-check',
    titleKey: 'droom.consent.purpose.dedup.t',
    descKey: 'droom.consent.purpose.dedup.d',
  },
};

export const KNOWN = (k: string): k is ConsentPurposeKey => k in PURPOSE_META;

export function ConsentGate({
  tenant,
  gate,
}: { tenant: TenantConfig; gate: ConsentGateState }): JSX.Element {
  const { t } = useT();
  const [affirmed, setAffirmed] = useState(false);
  const busy = gate.phase === 'posting' || gate.phase === 'captured';

  const toggle = () => setAffirmed((v) => !v);
  const onKey = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === ' ' || e.key === 'Enter') {
      e.preventDefault();
      toggle();
    }
  };

  // ── blocked: the customer declined — honest copy, no dead end (reconsider) ──
  if (gate.phase === 'declined') {
    return (
      <div className="app-view">
        <AppBar tenant={tenant} subtitle={t('droom.consent.subtitle')} />
        <div className="app-view center" style={{ paddingTop: 24 }}>
          <div className="center-col" style={{ maxWidth: '34ch' }}>
            <span className="consent-gate-badge">
              <Icon name="lock" size={28} />
            </span>
            <div className="h-title sm">{t('droom.consent.declined.title')}</div>
            <p className="sub" style={{ marginTop: 8 }}>
              {t('droom.consent.declined.body', { tenantName: tenant.name })}
            </p>
            <Button style={{ marginTop: 18 }} onClick={gate.reconsider}>
              {t('droom.consent.declined.review')}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // ── loading / error states ──
  if (gate.phase === 'loading' || gate.phase === 'error' || !gate.text) {
    return (
      <div className="app-view">
        <AppBar tenant={tenant} subtitle={t('droom.consent.subtitle')} />
        <div className="app-view center" style={{ paddingTop: 24 }}>
          <div className="center-col" style={{ maxWidth: '32ch' }}>
            {gate.phase === 'error' ? (
              <>
                <div className="h-title sm">{t('droom.consent.loadfail')}</div>
                <p className="sub" style={{ marginTop: 8 }}>
                  {gate.detail ? t(gate.detail) : t('droom.error.tryAgain')}
                </p>
                <Button style={{ marginTop: 18 }} onClick={gate.retry}>
                  {t('droom.action.tryAgain')}
                </Button>
              </>
            ) : (
              <p className="sub" role="status">
                {t('droom.consent.loading')}
              </p>
            )}
          </div>
        </div>
      </div>
    );
  }

  const text = gate.text;
  const purposes = text.purposes.filter(KNOWN);

  return (
    <div className="app-view">
      <AppBar tenant={tenant} subtitle={t('droom.consent.subtitle')} />

      <div className="stack">
        <div>
          <div className="h-title sm">{t('droom.consent.title')}</div>
          <p className="sub-sm" style={{ marginTop: 6 }}>
            {t('droom.consent.sub', { regime: tenant.regime })}
          </p>
        </div>

        <div className="card notice-summary">
          <div className="notice-head">
            <span style={{ fontWeight: 700, fontSize: 'var(--fs-cap)' }}>{t('droom.consent.process.head')}</span>
            <span
              className="v mono"
              title={
                text.ratified
                  ? t('droom.consent.version.title')
                  : t('droom.consent.version.unratified')
              }
            >
              {text.consentVersion}
            </span>
          </div>
          {purposes.map((k) => {
            const m = PURPOSE_META[k];
            return (
              <div className="purpose" key={k}>
                <span className="pic">
                  <Icon name={m.icon} size={16} />
                </span>
                <div>
                  <div className="pt">{t(m.titleKey)}</div>
                  <div className="pd">{t(m.descKey)}</div>
                </div>
              </div>
            );
          })}
        </div>

        {!text.ratified ? (
          <div className="consent-unratified mono" role="note">
            {t('droom.consent.unratified', { version: text.consentVersion })}
          </div>
        ) : null}

        <details className="disclosure">
          <summary>
            {t('droom.consent.readFull')}
            <span className="chev">
              <Icon name="chevron" size={16} />
            </span>
          </summary>
          <div className="body">{text.noticeBody}</div>
        </details>

        <div
          className="consent-toggle"
          data-on={affirmed ? 'true' : 'false'}
          role="checkbox"
          aria-checked={affirmed}
          tabIndex={0}
          onClick={toggle}
          onKeyDown={onKey}
        >
          <span className="box">
            <Icon name="check" size={14} />
          </span>
          <span className="ct">
            {rich(t('droom.consent.affirm', { tenantName: tenant.name }))}
          </span>
        </div>

        {gate.detail ? (
          <div className="consent-note mono" role="status">
            {t(gate.detail)}
          </div>
        ) : null}
      </div>

      <div className="spacer" />

      <div className="stack-sm" style={{ marginTop: 16 }}>
        <Button onClick={gate.affirm} disabled={!affirmed || busy}>
          {gate.phase === 'captured'
            ? t('droom.consent.recorded')
            : busy
              ? t('droom.consent.recording')
              : t('droom.consent.agree')}
        </Button>
        <Button variant="ghost" onClick={gate.decline} disabled={busy}>
          {t('droom.consent.decline')}
        </Button>
        <div className="cobrand mono" style={{ fontSize: 11 }}>
          {t('droom.consent.stampline')}
        </div>
      </div>
    </div>
  );
}
